import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { WinLossRow } from '@/hooks/useRevenueIntelligence';
import { formatUiNumber, formatUiPercent } from '@/lib/ui-format';

interface WinLossInsightsProps {
  rows: WinLossRow[];
}

const getOutcomeStyle = (outcome: string): string => {
  if (outcome === 'won') {
    return 'bg-status-success/10 text-status-success border-status-success/30';
  }
  if (outcome === 'lost') {
    return 'bg-status-danger/10 text-status-danger border-status-danger/30';
  }
  return 'bg-muted text-muted-foreground border-border';
};

export const WinLossInsights = ({ rows }: WinLossInsightsProps): JSX.Element => {
  if (rows.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="text-lg">Win/Loss Intelligence</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            No win/loss data yet
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-lg">Win/Loss Intelligence</CardTitle>
        <p className="text-sm text-muted-foreground">Patterns by methodology and reason</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.map((row, index) => (
          <div key={`${row.methodologyUsed}-${row.outcome}-${index}`} className="rounded-lg border border-border p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {row.methodologyUsed || 'Unknown'} · {row.primaryReason || 'General'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Avg cycle: {row.avgCycleDays
                    ? `${formatUiNumber(row.avgCycleDays, { maximumFractionDigits: 0 })}d`
                    : 'N/A'}{' '}
                  · Avg deal value:{' '}
                  {formatUiNumber(row.avgDealValue, {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0,
                  })}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Badge variant="outline" className={getOutcomeStyle(row.outcome)}>
                  {row.outcome.toUpperCase()}
                </Badge>
                <span className="text-sm font-semibold text-foreground">
                  {formatUiPercent(row.winRate / 100, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}{' '}
                  win rate
                </span>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
